import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import { Alert, Badge, Button, Card, LoadingState } from '../ui';
import { getProjects } from '../../services/projectService';
import {
  getActivityProjectAssignmentQuality,
  getUnassignedActivityReviews,
  updateActivityProject,
} from '../../services/activityReviewService';

const getPreview = (chain) => (
  chain.trigger_message_text || chain.message_text || 'Activity without stored trigger text'
);

const UnassignedActivityQueue = () => {
  const [selectedProjects, setSelectedProjects] = useState({});
  const queryClient = useQueryClient();
  const queueQuery = useQuery(
    ['unassignedActivityReviews'],
    () => getUnassignedActivityReviews({ limit: 100, offset: 0 }),
    { refetchOnMount: 'always', refetchInterval: 30000 },
  );
  const projectsQuery = useQuery(['activeProjectsForActivityAssignment'], getProjects, {
    select: (projects) => (Array.isArray(projects) ? projects.filter((project) => project.is_active !== false) : []),
  });
  const qualityQuery = useQuery(
    ['activityProjectAssignmentQuality'],
    getActivityProjectAssignmentQuality,
    { refetchOnMount: 'always' },
  );

  const projects = projectsQuery.data || [];
  const activities = useMemo(() => queueQuery.data?.reviews || [], [queueQuery.data]);
  const mutation = useMutation(
    ({ chain, projectId }) => updateActivityProject(chain.id, {
      projectId,
      revision: Number(chain.assignment_revision),
    }),
    {
      onSuccess: (_data, variables) => {
        const project = projects.find((item) => Number(item.id) === Number(variables.projectId));
        toast.success(project ? `Activity moved to ${project.name}.` : 'Activity left without a project.');
        queryClient.invalidateQueries(['unassignedActivityReviews']);
        queryClient.invalidateQueries(['unassignedActivityReviewCount']);
        queryClient.invalidateQueries(['activityReviews']);
        queryClient.invalidateQueries(['activityReviewStats']);
        queryClient.invalidateQueries(['agentChains']);
        queryClient.invalidateQueries(['activityProjectAssignmentQuality']);
      },
      onError: async (error) => {
        if (error.response?.status === 409) {
          await queryClient.refetchQueries(['unassignedActivityReviews']);
          toast.info('This assignment changed. The latest version is shown.');
          return;
        }
        toast.error('Project was not saved. Your selection is still here.');
      },
    },
  );

  if (queueQuery.isLoading || projectsQuery.isLoading) return <LoadingState message="Loading activities that need a project..." />;
  if (queueQuery.isError || projectsQuery.isError) {
    return <Alert variant="error" title="Project assignment inbox could not be loaded">Nothing was changed. Try again.</Alert>;
  }

  const quality = qualityQuery.data;
  return (
    <div className="space-y-4">
      <Alert variant="info" title="One inbox prevents duplicate project activity">
        These completed chains are not shown inside any project. Choose one project only, or leave the item here until there is enough information.
      </Alert>

      {quality && (quality.agreementRate != null || quality.boundaryAgreementRate != null) && (
        <Card className="p-4">
          <p className="text-sm font-semibold text-gray-900">Checked assignment quality</p>
          {quality.agreementRate != null && (
            <p className="mt-1 text-sm text-gray-600">
              People kept the suggested project in {Math.round(quality.agreementRate * 100)}% of {quality.reviewedAiAssignments} reviewed AI assignments.
            </p>
          )}
          {quality.boundaryAgreementRate != null && (
            <p className="mt-1 text-sm text-gray-600">
              People kept the detected message boundary in {Math.round(quality.boundaryAgreementRate * 100)}% of {quality.reviewedBoundaries} reviewed chains.
            </p>
          )}
        </Card>
      )}

      {activities.length === 0 ? (
        <Card className="p-10 text-center">
          <p className="text-lg font-semibold text-gray-900">No activities need a project</p>
          <p className="mt-2 text-sm text-gray-500">Ambiguous or unmatched completed chains will appear here.</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {activities.map((chain) => {
            const selectedValue = selectedProjects[chain.id] ?? chain.suggested_project_id ?? '';
            const candidates = Array.isArray(chain.candidate_projects) ? chain.candidate_projects : [];
            return (
              <Card key={chain.id} className="p-4">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-center">
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-2">
                      <Badge variant={chain.assignment_status === 'ambiguous' ? 'yellow' : 'gray'}>
                        {chain.boundary_status === 'needs_review'
                          ? 'Chain boundary needs review'
                          : chain.assignment_status === 'ambiguous'
                            ? 'Several projects may fit'
                          : chain.assignment_status === 'failed'
                            ? 'Assignment temporarily failed'
                            : 'No project match'}
                      </Badge>
                      <span className="text-xs text-gray-500">
                        {chain.ended_at ? new Date(chain.ended_at).toLocaleString() : 'Completed activity'}
                      </span>
                      <span className="text-xs text-gray-500">{chain.message_count || 1} message{Number(chain.message_count || 1) === 1 ? '' : 's'}</span>
                    </div>
                    <p className="mt-2 truncate text-sm font-semibold text-gray-900">{chain.chat_title || chain.trigger_chat_id || 'Unknown chat'}</p>
                    <p className="mt-1 line-clamp-2 text-sm text-gray-600">{getPreview(chain)}</p>
                    {chain.assignment_reason_summary && (
                      <p className="mt-2 text-xs text-gray-500">{chain.assignment_reason_summary}</p>
                    )}
                    {candidates.length > 0 && (
                      <p className="mt-2 text-xs text-gray-500">Source-chat candidates: {candidates.map((project) => project.name).join(', ')}</p>
                    )}
                  </div>

                  <div className="flex w-full flex-col gap-2 sm:flex-row lg:w-auto">
                    <label className="min-w-0 flex-1 lg:w-72">
                      <span className="sr-only">Choose project for this activity</span>
                      <select
                        value={selectedValue}
                        onChange={(event) => setSelectedProjects((current) => ({
                          ...current,
                          [chain.id]: event.target.value,
                        }))}
                        className="min-h-[44px] w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                      >
                        <option value="">Choose an active project</option>
                        {projects.map((project) => <option key={project.id} value={project.id}>{project.name}</option>)}
                      </select>
                    </label>
                    <Button
                      variant="primary"
                      className="min-h-[44px]"
                      disabled={!selectedValue || mutation.isLoading}
                      onClick={() => mutation.mutate({ chain, projectId: Number(selectedValue) })}
                    >
                      Save project
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default UnassignedActivityQueue;
